import MotionWrapper from '@atoms/MotionWrapper/MotionWrapper';
import type { IPricingData } from '@models/IPricingData';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import classNames from 'classnames';
import type { MotionProps } from 'framer-motion';

interface Props {
  readonly centerText?: boolean;
  readonly fromPriceLabel?: string;
  readonly pricingData: IPricingData;
  readonly vatLabel?: string;
  readonly shippingLabel?: string;
  readonly rrpLabel?: string;
  readonly smallText?: boolean;
  readonly containerClassName?: string;
  readonly containerFontSize?: string;
  readonly taxesAnimationProps?: MotionProps;
  readonly taxesTopMarginClassName?: string;
  readonly taxesClassName?: string;
  readonly taxesFontSize?: string;
  readonly priceClassName?: string;
  readonly dataTestId?: string;
  readonly isLightMode?: boolean;
}

const PriceDisplayText = ({
  fromPriceLabel,
  pricingData,
  vatLabel,
  shippingLabel,
  rrpLabel,
  centerText = false,
  smallText = false,
  containerClassName,
  containerFontSize = 'body-one',
  taxesAnimationProps = {},
  taxesTopMarginClassName = 'mt-2',
  taxesClassName,
  taxesFontSize = 'body-four',
  priceClassName,
  dataTestId,
  isLightMode,
}: Props) => {
  const { initialPriceAriaLabel, currentPriceAriaLabel } = useAriaLabels();

  const {
    amountSaved,
    formattedPrice,
    formattedCalculatedPrice,
    shippingCost,
    price,
  } = pricingData;

  if (typeof price !== 'number') {
    return null;
  }

  const hasDiscount = amountSaved > 0 && !!formattedPrice;
  const priceContainerAriaLabel =
    [
      !hasDiscount && fromPriceLabel,
      hasDiscount && rrpLabel,
      hasDiscount && `${initialPriceAriaLabel} ${formattedPrice},`,
      hasDiscount && formattedCalculatedPrice && currentPriceAriaLabel,
      formattedCalculatedPrice,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div
      className={classNames(
        containerClassName,
        smallText ? 'body-two font-light' : containerFontSize,
        {
          'flex flex-col items-center': centerText,
        }
      )}
      data-testid={dataTestId}
    >
      {(hasDiscount || formattedCalculatedPrice) && (
        // biome-ignore lint/a11y/useSemanticElements: Adding role="group" for better screen reader experience, as this container groups related price information together, including the original price, discounted price, and any relevant labels. This helps to ensure that screen reader users understand the relationship between the different pieces of information being displayed.
        <div
          role="group"
          className={classNames('flex flex-wrap gap-x-2', {
            'justify-center': centerText,
          })}
          aria-label={priceContainerAriaLabel}
        >
          {fromPriceLabel && <span aria-hidden="true">{fromPriceLabel}</span>}

          {hasDiscount && (
            <>
              {rrpLabel && (
                <span aria-hidden="true" className="mr-2">
                  {rrpLabel}
                </span>
              )}

              <span
                aria-hidden="true"
                className="font-extralight line-through decoration-1"
                data-testid="Price-Display-Text-Discount-Price"
              >
                {formattedPrice}
              </span>
            </>
          )}

          {formattedCalculatedPrice && (
            <span
              aria-hidden={fromPriceLabel || hasDiscount ? 'true' : undefined}
              className={classNames(
                {
                  'font-normal': !smallText,
                  'text-grey-450': amountSaved > 0,
                  'dark:text-yellow': amountSaved > 0 && !isLightMode,
                },
                priceClassName
              )}
              data-testid="Price-Display-Text-Price"
            >
              {formattedCalculatedPrice}
            </span>
          )}
        </div>
      )}

      {(vatLabel || (!shippingCost && shippingLabel)) && (
        <MotionWrapper
          tag="p"
          {...taxesAnimationProps}
          className={classNames(taxesClassName, taxesFontSize, 'block', {
            [`${taxesTopMarginClassName}`]: !smallText,
            'text-center': centerText,
          })}
          data-testid="Price-Display-Text-VAT/Shipping"
        >
          {vatLabel && vatLabel}
          {vatLabel && !shippingCost && shippingLabel && ' + '}
          {!shippingCost && shippingLabel && shippingLabel}
        </MotionWrapper>
      )}
    </div>
  );
};

export default PriceDisplayText;
